import { useState, useMemo } from "react";
import {
  MessageSquare,
  CheckCircle2,
  Reply,
  ChevronDown,
  ChevronRight,
  Loader2,
} from "lucide-react";
import { cn } from "@/lib/utils";
import { useAuthStore } from "@/stores/auth-store";
import { useStudyBuilderStore, selectIsReadOnly } from "@/stores/study-builder-store";
import {
  useReviewComments,
  useAddReviewComment,
  useResolveComment,
  useBuildReviews,
} from "@/hooks/use-protocol-builder";
import type { ReviewComment } from "@/api/protocol-types";
import { MentionInput } from "./MentionInput";

interface ReviewCommentThreadProps {
  artifactType: string;
  artifactId: string;
  fieldPath?: string;
}

export function ReviewCommentThread({
  artifactType,
  artifactId,
  fieldPath,
}: ReviewCommentThreadProps) {
  const buildId = useStudyBuilderStore((s) => s.buildId);
  const isReadOnly = useStudyBuilderStore(selectIsReadOnly);
  const { data: commentsData, isLoading } = useReviewComments(
    buildId ?? undefined,
    artifactType,
    artifactId,
  );
  const { data: reviewsData } = useBuildReviews(buildId ?? undefined);
  const addComment = useAddReviewComment();
  const resolveComment = useResolveComment();

  const [isExpanded, setIsExpanded] = useState(true);
  const [newComment, setNewComment] = useState("");
  const [replyingTo, setReplyingTo] = useState<string | null>(null);

  // Find active review for this artifact
  const review = reviewsData?.reviews?.find(
    (r) =>
      r.artifactType === artifactType &&
      r.artifactId === artifactId,
  );

  const allComments = commentsData?.comments ?? [];

  // Filter by fieldPath if provided
  const filteredComments = fieldPath
    ? allComments.filter((c) => c.fieldPath === fieldPath)
    : allComments;

  const unresolvedCount = filteredComments.filter((c) => !c.resolved).length;

  // Build thread tree: top-level comments and their children
  const { topLevel, childMap } = useMemo(() => {
    const top: ReviewComment[] = [];
    const children: Record<string, ReviewComment[]> = {};
    for (const comment of filteredComments) {
      if (comment.parentCommentId) {
        if (!children[comment.parentCommentId]) {
          children[comment.parentCommentId] = [];
        }
        children[comment.parentCommentId].push(comment);
      } else {
        top.push(comment);
      }
    }
    return { topLevel: top, childMap: children };
  }, [filteredComments]);

  const handleAddComment = (parentCommentId?: string, mentionedUids?: string[], referencedFields?: Array<{fieldId: string; crfId: string; domain: string; variable: string}>) => {
    if (!buildId || !newComment.trim()) return;
    addComment.mutate(
      {
        buildId,
        artifactType,
        artifactId,
        content: newComment.trim(),
        fieldPath,
        parentCommentId,
        reviewId: review?.id,
        mentionedUids,
        referencedFields,
      },
      {
        onSuccess: () => {
          setNewComment("");
          setReplyingTo(null);
        },
      },
    );
  };

  const handleResolve = (commentId: string) => {
    if (!buildId) return;
    resolveComment.mutate({ buildId, commentId });
  };

  if (!buildId) return null;

  return (
    <div className="rounded-lg border border-border">
      {/* Header */}
      <button
        onClick={() => setIsExpanded(!isExpanded)}
        className="flex w-full items-center gap-2 px-3 py-2 text-left transition-colors hover:bg-muted/50"
      >
        {isExpanded ? (
          <ChevronDown className="h-3.5 w-3.5 text-muted-foreground" />
        ) : (
          <ChevronRight className="h-3.5 w-3.5 text-muted-foreground" />
        )}
        <MessageSquare className="h-3.5 w-3.5 text-muted-foreground" />
        <span className="text-xs font-medium text-foreground">
          Comments
        </span>
        {filteredComments.length > 0 && (
          <span className="text-[10px] text-muted-foreground">
            ({filteredComments.length})
          </span>
        )}
        {unresolvedCount > 0 && (
          <span className="ml-auto rounded-full bg-amber-500/10 px-1.5 py-0.5 text-[10px] font-medium text-amber-600 dark:text-amber-400">
            {unresolvedCount} open
          </span>
        )}
      </button>

      {isExpanded && (
        <div className="border-t border-border">
          {/* Comment list */}
          {isLoading ? (
            <div className="flex items-center justify-center py-4">
              <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />
            </div>
          ) : topLevel.length === 0 ? (
            <div className="px-3 py-4 text-center text-[11px] text-muted-foreground">
              No comments yet. Start the discussion below.
            </div>
          ) : (
            <div className="divide-y divide-border">
              {topLevel.map((comment) => (
                <CommentItem
                  key={comment.id}
                  comment={comment}
                  replies={childMap[comment.id] ?? []}
                  onResolve={handleResolve}
                  onReply={(id) => setReplyingTo(id)}
                  replyingTo={replyingTo}
                  replyValue={newComment}
                  onReplyChange={setNewComment}
                  onSubmitReply={(mentionedUids, referencedFields) =>
                    handleAddComment(replyingTo ?? undefined, mentionedUids, referencedFields)
                  }
                  isSubmitting={addComment.isPending}
                  isReadOnly={isReadOnly}
                />
              ))}
            </div>
          )}

          {/* New comment input */}
          {!isReadOnly && !replyingTo && (
            <div className="border-t border-border p-3">
              <div className="flex gap-2">
                <MentionInput
                  value={newComment}
                  onChange={setNewComment}
                  onSubmit={(mentionedUids, referencedFields) =>
                    handleAddComment(undefined, mentionedUids, referencedFields)
                  }
                  placeholder="Add a comment... (@ to mention)"
                  disabled={addComment.isPending}
                />
                <button
                  onClick={() => handleAddComment()}
                  disabled={!newComment.trim() || addComment.isPending}
                  className="rounded-md bg-primary px-3 py-1.5 text-[11px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
                >
                  {addComment.isPending ? (
                    <Loader2 className="h-3 w-3 animate-spin" />
                  ) : (
                    "Comment"
                  )}
                </button>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function CommentItem({
  comment,
  replies,
  onResolve,
  onReply,
  replyingTo,
  replyValue,
  onReplyChange,
  onSubmitReply,
  isSubmitting,
  isReadOnly,
}: {
  comment: ReviewComment;
  replies: ReviewComment[];
  onResolve: (id: string) => void;
  onReply: (id: string | null) => void;
  replyingTo: string | null;
  replyValue: string;
  onReplyChange: (v: string) => void;
  onSubmitReply: (mentionedUids: string[], referencedFields?: Array<{fieldId: string; crfId: string; domain: string; variable: string}>) => void;
  isSubmitting: boolean;
  isReadOnly?: boolean;
}) {
  const uid = useAuthStore((s) => s.uid);

  const timeAgo = formatTimeAgo(comment.createdAt);

  return (
    <div className={cn("px-3 py-2.5", comment.resolved && "opacity-60")}>
      <div className="flex items-start gap-2">
        {/* Author avatar */}
        <div className="flex h-5 w-5 shrink-0 items-center justify-center rounded-full bg-muted text-[8px] font-bold text-muted-foreground">
          {comment.authorName
            .split(" ")
            .map((n) => n[0])
            .join("")
            .slice(0, 2)
            .toUpperCase()}
        </div>

        <div className="min-w-0 flex-1">
          {/* Author info */}
          <div className="flex items-center gap-1.5">
            <span className="text-[11px] font-medium text-foreground">
              {comment.authorName}
            </span>
            <span className="rounded-full border border-border bg-muted px-1.5 py-0 text-[9px] text-muted-foreground">
              {comment.authorRole}
            </span>
            <span className="text-[10px] text-muted-foreground">
              {timeAgo}
            </span>
            {comment.fieldPath && (
              <span className="rounded bg-violet-500/10 px-1 py-0 text-[9px] text-violet-600 dark:text-violet-400">
                {comment.fieldPath.split(".").pop()}
              </span>
            )}
          </div>

          {/* Content */}
          <p
            className={cn(
              "mt-0.5 text-xs text-foreground",
              comment.resolved && "line-through",
            )}
          >
            <CommentContent text={comment.content} />
          </p>

          {/* Actions */}
          <div className="mt-1 flex items-center gap-2">
            {!isReadOnly && !comment.resolved && (
              <>
                <button
                  onClick={() => onReply(comment.id)}
                  className="flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-foreground"
                >
                  <Reply className="h-2.5 w-2.5" />
                  Reply
                </button>
                {(comment.authorUid === uid) && (
                  <button
                    onClick={() => onResolve(comment.id)}
                    className="flex items-center gap-0.5 text-[10px] text-muted-foreground hover:text-emerald-600"
                  >
                    <CheckCircle2 className="h-2.5 w-2.5" />
                    Resolve
                  </button>
                )}
              </>
            )}
            {comment.resolved && (
              <span className="flex items-center gap-0.5 text-[10px] text-emerald-600">
                <CheckCircle2 className="h-2.5 w-2.5" />
                Resolved
              </span>
            )}
          </div>
        </div>
      </div>

      {/* Replies */}
      {replies.length > 0 && (
        <div className="ml-7 mt-2 flex flex-col gap-2 border-l-2 border-border pl-3">
          {replies.map((reply) => (
            <div key={reply.id} className="flex items-start gap-2">
              <div className="flex h-4 w-4 shrink-0 items-center justify-center rounded-full bg-muted text-[7px] font-bold text-muted-foreground">
                {reply.authorName
                  .split(" ")
                  .map((n) => n[0])
                  .join("")
                  .slice(0, 2)
                  .toUpperCase()}
              </div>
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="text-[10px] font-medium text-foreground">
                    {reply.authorName}
                  </span>
                  <span className="text-[9px] text-muted-foreground">
                    {formatTimeAgo(reply.createdAt)}
                  </span>
                </div>
                <p className="text-[11px] text-foreground">
                  <CommentContent text={reply.content} />
                </p>
              </div>
            </div>
          ))}
        </div>
      )}

      {/* Reply input */}
      {replyingTo === comment.id && (
        <div className="ml-7 mt-2 flex gap-2">
          <MentionInput
            value={replyValue}
            onChange={onReplyChange}
            onSubmit={onSubmitReply}
            placeholder="Write a reply... (@ to mention)"
            disabled={isSubmitting}
            autoFocus
            className="flex-1 rounded-md border border-border bg-background px-2 py-1 text-[11px] text-foreground placeholder:text-muted-foreground"
          />
          <button
            onClick={() => onSubmitReply([])}
            disabled={!replyValue.trim() || isSubmitting}
            className="rounded-md bg-primary px-2 py-1 text-[10px] font-medium text-primary-foreground hover:bg-primary/90 disabled:opacity-50"
          >
            Reply
          </button>
          <button
            onClick={() => onReply(null)}
            className="text-[10px] text-muted-foreground hover:text-foreground"
          >
            Cancel
          </button>
        </div>
      )}
    </div>
  );
}

/** Renders comment text with @mentions and #field-tags highlighted. */
function CommentContent({ text }: { text: string }) {
  const parts = text.split(/(@[A-Z]\w+(?:\s+[A-Z]\w+)*|#\w+(?:\.\w+)?)/g);
  return (
    <>
      {parts.map((part, i) =>
        part.startsWith("@") ? (
          <span
            key={i}
            className="font-semibold text-primary"
          >
            {part}
          </span>
        ) : part.startsWith("#") ? (
          <span
            key={i}
            className="rounded bg-violet-500/10 px-1 py-0.5 font-mono text-[10px] font-semibold text-violet-600 dark:text-violet-400"
          >
            {part}
          </span>
        ) : (
          <span key={i}>{part}</span>
        ),
      )}
    </>
  );
}

function formatTimeAgo(dateStr: string): string {
  const date = new Date(dateStr);
  const now = new Date();
  const diffMs = now.getTime() - date.getTime();
  const diffMin = Math.floor(diffMs / 60000);

  if (diffMin < 1) return "just now";
  if (diffMin < 60) return `${diffMin}m ago`;
  const diffHr = Math.floor(diffMin / 60);
  if (diffHr < 24) return `${diffHr}h ago`;
  const diffDay = Math.floor(diffHr / 24);
  if (diffDay < 7) return `${diffDay}d ago`;
  return date.toLocaleDateString();
}
